import { Bot, CloudOff, FolderHeart, Pencil, Plus, Trash } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useFavorites } from '../api/queries'
import type { FavoriteGroup } from '../api/types'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { DeleteGroupDialog, GroupNameDialog } from '../components/favorites/GroupDialogs'
import { PageHeader } from '../components/PageHeader'
import { Spinner } from '../components/Spinner'
import { formatAgo, plural } from '../lib/format'
import { useViewState } from '../lib/navigation'

type DialogState = { kind: 'create' } | { kind: 'rename'; group: FavoriteGroup } | { kind: 'delete'; group: FavoriteGroup }

export function FavoritesPage() {
  const groups = useFavorites()
  const navigate = useNavigate()
  const [dialogRef, setDialogRef] = useViewState<{ kind: DialogState['kind']; id?: number } | null>('favorites.dialog', null)
  const setDialog = (value: DialogState | null) => setDialogRef(value ? { kind: value.kind, ...('group' in value ? { id: value.group.id } : {}) } : null)
  const selected = groups.data?.find((group) => group.id === dialogRef?.id)
  const dialog: DialogState | null = dialogRef?.kind === 'create' ? { kind: 'create' }
    : dialogRef && selected && (dialogRef.kind === 'rename' || dialogRef.kind === 'delete') ? { kind: dialogRef.kind, group: selected } : null
  const now = Date.now()
  const list = groups.data ?? []

  return (
    <>
      <PageHeader
        title="Избранное"
        description={
          list.length > 0
            ? `${list.length} ${plural(list.length, 'группа', 'группы', 'групп')} · ссылку на группу можно отдать агенту`
            : 'Группы постов для сценариев и ссылка для агента.'
        }
        actions={
          <Button variant="primary" size="sm" onClick={() => setDialog({ kind: 'create' })}>
            <Plus size={14} />
            Группа
          </Button>
        }
      />

      {groups.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : groups.isError ? (
        <EmptyState icon={CloudOff} title="Не удалось загрузить избранное">
          <Button className="mt-3" onClick={() => void groups.refetch()}>
            Повторить
          </Button>
        </EmptyState>
      ) : list.length === 0 ? (
        <>
          <EmptyState icon={FolderHeart} title="Групп пока нет">
            Откройте пост в «Залётах», нажмите ♥ и сложите его в группу, например «На неделю». Группу можно смотреть
            каруселью.
          </EmptyState>
          <div className="mx-auto flex max-w-md items-start gap-3 rounded-xl bg-surface p-3.5 text-sm ring-1 ring-line ring-inset">
            <Bot size={18} className="mt-0.5 shrink-0 text-muted" />
            <div>
              <div className="font-medium">Ссылка для агента</div>
              <p className="mt-0.5 text-muted">
                Группа одной ссылкой: метрики, подпись, транскрипт и раскадровка каждого поста. Её можно отдать
                Codex CLI.
              </p>
            </div>
          </div>
        </>
      ) : (
        <ul className="grid grid-cols-2 gap-x-2.5 gap-y-4 pt-2 desk:grid-cols-[repeat(auto-fill,minmax(184px,1fr))] desk:gap-x-5 desk:gap-y-7">
          {list.map((g) => (
            <GroupCard
              key={g.id}
              group={g}
              now={now}
              onRename={() => setDialog({ kind: 'rename', group: g })}
              onDelete={() => setDialog({ kind: 'delete', group: g })}
            />
          ))}
        </ul>
      )}

      {dialog?.kind === 'create' && (
        <GroupNameDialog onClose={() => setDialog(null)} onCreated={(g) => void navigate(`/favorites/${g.id}`)} />
      )}
      {dialog?.kind === 'rename' && <GroupNameDialog group={dialog.group} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'delete' && <DeleteGroupDialog group={dialog.group} onClose={() => setDialog(null)} />}
    </>
  )
}

/** Группа «стопкой»: обложка последнего добавленного, под ней ещё листы, если постов несколько. */
function GroupCard({
  group: g,
  now,
  onRename,
  onDelete,
}: {
  group: FavoriteGroup
  now: number
  onRename: () => void
  onDelete: () => void
}) {
  const [broken, setBroken] = useState(false)
  const cover = g.cover_url && !broken ? g.cover_url : null
  return (
    <li className="group/card relative min-w-0">
      <Link to={`/favorites/${g.id}`} className="block rounded-xl outline-offset-2">
        <div className="relative pt-2">
          {g.count > 2 && (
            <div className="absolute inset-x-4 top-0 bottom-4 rounded-xl bg-line ring-1 ring-line ring-inset" aria-hidden />
          )}
          {g.count > 1 && (
            <div className="absolute inset-x-2 top-1 bottom-4 rounded-xl bg-surface ring-1 ring-line ring-inset" aria-hidden />
          )}
          <div className="relative aspect-[1.15] overflow-hidden rounded-xl desk:aspect-[4/5] bg-surface ring-1 ring-line transition-[box-shadow] ring-inset group-hover/card:ring-fg/25">
            {cover ? (
              <img
                src={cover}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setBroken(true)}
                className="absolute inset-0 size-full object-cover"
              />
            ) : (
              <div className="absolute inset-x-0 top-0 bottom-1/3 flex items-center justify-center text-link">
                <FolderHeart size={28} strokeWidth={1.5} />
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/90 via-black/60 to-transparent px-2.5 pt-5 pb-2.5 text-white">
              <div className="line-clamp-2 text-sm leading-tight font-semibold wrap-anywhere">{g.name}</div>
              <span className="mt-1 block text-[11px] tabular-nums">
                {g.count} {plural(g.count, 'пост', 'поста', 'постов')}
              </span>
            </div>
            {g.agent_url && (
              <span
                className="absolute top-2 left-2 inline-flex size-6 items-center justify-center rounded-md bg-black/60 text-white"
                aria-hidden
              >
                <Bot size={14} />
              </span>
            )}
          </div>
        </div>
        <div className="mt-2 min-w-0 px-0.5">
          <div className="mt-0.5 truncate text-xs text-muted">{formatAgo(g.updated_at, now)}</div>
          {/* Значок 🤖 на обложке объяснён словами: наведения на телефоне нет. */}
          {g.agent_url && (
            <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted">
              <Bot size={12} className="shrink-0" aria-hidden />
              ссылка для агента
            </div>
          )}
        </div>
      </Link>
      {/* На компьютере правка прямо в сетке, на телефоне — внутри группы. */}
      <div className="absolute top-3.5 right-1.5 hidden gap-1 opacity-0 transition-opacity group-focus-within/card:opacity-100 group-hover/card:opacity-100 desk:flex">
        <IconAction label="Переименовать" onClick={onRename}>
          <Pencil size={14} />
        </IconAction>
        <IconAction label="Удалить группу" onClick={onDelete}>
          <Trash size={14} />
        </IconAction>
      </div>
    </li>
  )
}

function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="inline-flex size-7 items-center justify-center rounded-md bg-white/90 text-black shadow-sm hover:bg-white"
    >
      {children}
    </button>
  )
}
